import { mapState } from 'vuex';
import pluralize from 'pluralize';
import { DelayLoader } from '@/helpers';

export default resourceName => {
    const singularResourceName = pluralize.singular(resourceName);
    const upperSingularResourceName = singularResourceName.charAt(0).toUpperCase() + singularResourceName.slice(1);
    return ({
        data() {
            return {
                loader: new DelayLoader(),
                errors: null,
            };
        },
        computed: {
            ...mapState({
                /**
                 * Gets the resource from the correct vuex module
                 */
                [singularResourceName]: state => state[resourceName][singularResourceName],
            }),
        },
        created() {
            this[`retrieve${upperSingularResourceName}`]();
        },
        watch: {
            /**
             * If the route parameter changes fetch the new resource
             */
            '$route.params.id'() {
                this[`retrieve${upperSingularResourceName}`]();
            },
        },
        methods: {
            /**
             * Checks the state if we have the resource if not fetches it
             */
            [`retrieve${upperSingularResourceName}`]() {
                // Check if we already have the resource in our state then we don't need to fetch it again
                if (this[singularResourceName].id === this.$route.params.id) {
                    return;
                }

                this.loader.start();

                this.$store
                    .dispatch(`${resourceName}/fetch`, this.$route.params.id)
                    .finally(() => this.loader.reset());
            },

            /**
             * Updates the resource
             * @param {Object} resource
             */
            [`update${upperSingularResourceName}`](resource) {
                this.loader.start();

                this.$store
                    .dispatch(`${resourceName}/update`, {
                        id: this.$route.params.id,
                        resource: resource,
                    })
                    .then(() => this.$router.push({
                        name: `${resourceName}.view`,
                        params: { id: this.$route.params.id },
                    }))
                    .catch(this.assignErrors)
                    .finally(() => this.loader.reset());
            },

            /**
             * Stores the resource
             * @param {Object} resource
             */
            [`store${upperSingularResourceName}`](resource) {
                this.loader.start();

                this.$store
                    .dispatch(`${resourceName}/save`, resource)
                    .then(({ id }) =>
                        this.$router.push({
                            name: `${resourceName}.view`,
                            params: { id },
                        }),
                    )
                    .catch(this.assignErrors)
                    .finally(() => this.loader.reset());
            },

            /**
             * Deletes the resource
             */
            [`delete${upperSingularResourceName}`]() {
                this.loader.start();

                this.$store
                    .dispatch(`${resourceName}/delete`, this.$route.params.id)
                    .then(() => this.$router.push({ name: resourceName }))
                    .finally(() => this.loader.reset());
            },
            /**
             * Opens the edit page for the resource
             */
            [`edit${upperSingularResourceName}`]() {
                this.$router.push({
                    name: `${resourceName}.edit`,
                    params: { id: this.$route.params.id },
                });
            },
            assignErrors(errors) {
                this.errors = errors;
            },
        },
    });
};
